import {
  Activity,
  BookCopy,
  Building2,
  CalendarClock,
  CalendarRange,
  ChartColumn,
  ClipboardList,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  ListTodo,
  MapPinned,
  Repeat,
  ScrollText,
  SlidersHorizontal,
  Truck,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  ARRANGEMENT_TYPE_KEYS,
  ARRANGEMENT_TYPES,
  type AccessRole,
  type ArrangementType,
  type OfficerFlag,
} from '@pams/shared';
import { typedListHref } from '@/lib/templateLinks';
import {
  ANALYTICS_ACCESS,
  AUDIT_ACCESS,
  LOCATION_INTEL_ACCESS,
  mayAuthorArrangement,
  mayAuthorPolicy,
  mayReadDutyAnalyticsAt,
  mayUseTemplatesAt,
  meetsRequirement,
  type Capability,
  type CapabilityRequirement,
} from '@/lib/accessRole';

/**
 * THE NAV MODEL. Data, not markup.
 *
 * It lives in `lib/` rather than beside `AppSidebar` for a mechanical reason:
 * `react-refresh/only-export-components` is configured as a warning and `pnpm lint` runs
 * with `--max-warnings 0`, so a component file that also exports a constant fails the
 * build outside `components/ui/**`.
 *
 * ⚠️ NAV VISIBILITY IS DRIVEN BY THE CAPABILITY TABLE, NEVER BY A ROLE STRING.
 * `visibleSections` fails closed: no role, or a capability the role lacks, and the entry
 * is not rendered. None of this is enforcement — the API checks the same table, and
 * subtree scoping is enforced in the query itself.
 *
 * ─── ⚠️ [amended C1g.1] AND `superAdminOnly` IS A NARROWING ON TOP, WHICH IS NOT THE SAME
 *     THING AS DRIVING VISIBILITY FROM A ROLE ────────────────────────────────────────
 *
 * The sentence above stays true in the sense it was written: the capability filter still runs
 * FIRST and still fails closed, and nothing here can make a row appear for a role the table
 * refuses. What is added is a second, purely SUBTRACTIVE gate — it can only ever hide MORE.
 * That asymmetry is the whole reason it is safe, and it is structural rather than a promise:
 * `visibleSections` ANDs it with `allowed(capability)`, so there is no expression through
 * which it could grant.
 *
 * ⚠️ IT IS PRESENTATION, EXACTLY AS `labelByRole` IS. [C1f §9] already established a per-role
 * value in this file, with the line that governs both: "NAV VISIBILITY IS NOT AUTHORISATION,
 * AND NEITHER IS A NAV LABEL." **Nothing about the API changed** — a nodal still holds
 * `org.read`, `place.read` and `policy.read`, still needs all three (the arrangement builder's
 * unit picker, its place autocomplete and a duty point's columns), and can still open any of
 * these screens by typing the URL. This hides the DOORS, and it was asked for as a UI change.
 *
 * ⚠️ A BOOLEAN NAMING ONE ROLE, NEVER A ROLE LIST. `visibleForRoles: AccessRole[]` reads as
 * the more general design and is the worse one: it can be widened by editing an array in a
 * NAV file, which is how a nav rule starts being used as a permission rule. A boolean says one
 * thing, and a second kind of narrowing is a decision with a reviewer attached — [P6b]'s "two
 * named columns, never one bag keyed `<A>#<B>#<FIELD>`", one layer up.
 *
 * ⚠️ [amended C5c §3] ONE ROLE, OR ONE PREDICATE IMPORTED FROM `accessRole.ts`. `policyAuthorsOnly`
 * is the second kind, and it is still a boolean — it names `mayAuthorPolicy`, whose body lives in
 * the file every offer gate is reviewed in, exactly as the `+ New arrangement` action names
 * `mayAuthorArrangement` below. It is ANDed like `superAdminOnly`, so it too can only subtract.
 */

export type NavLeaf = {
  kind: 'leaf';
  to: string;
  label: string;
  /**
   * ⚠️ [C1f §9] ONE ROLE'S OWN WORD FOR THIS ROW, resolved by `visibleSections`.
   *
   * The FIRST per-role label in this nav, and it stays a `Partial` record for a reason: an
   * unlisted role keeps `label`, so adding an `AccessRole` is not a compile error in a table
   * about WORDS. `AppSidebar` renders `entry.label` and needs no change — the resolution
   * happens before it, so the palette (`visibleDestinations`) inherits it for free.
   *
   * ⚠️ NAV VISIBILITY IS NOT AUTHORISATION, AND NEITHER IS A NAV LABEL. The route enforces
   * server-side regardless of what this row says.
   */
  labelByRole?: Partial<Record<AccessRole, string>>;
  icon: LucideIcon;
  /**
   * Undefined means "visible to anyone who reached the shell at all".
   *
   * ⚠️ [amended C5b §5 · AUDIT-FLAG] A `CapabilityRequirement`, so a row may also name a FLAG
   * capability. TWO rows do, both in System: Usage analytics takes `ANALYTICS_ACCESS` and Audit log
   * takes `AUDIT_ACCESS`, each the value its own route and API route take. [amended LOCATION-INTEL] THREE:
   * Locational Intel, in Overview, takes `LOCATION_INTEL_ACCESS`. `visibleSections` asks
   * `meetsRequirement` with the officer's flags; a flag can only ADD to what the role allows, so no
   * value here can reveal a row the role is refused.
   */
  capability?: CapabilityRequirement;
  /**
   * ⚠️ [C1g.1] HIDE THIS ROW FROM EVERY ROLE BUT `SUPER_ADMIN` — A UI DECISION, NOT A GRANT.
   *
   * Confirmed with the client: a nodal's sidebar carries the dashboard, the two arrangement
   * lists and Officers, and nothing else. [amended P7B.1 §4] THREE arrangement lists now — a typed
   * arrangement ("Durga Puja PA") left Manage Arrangements' tab for a row of its own, and keeps the
   * reach the tab had. The establishment (Organisation, Places), the
   * configuration masters and the round-the-clock generator are the CP office's own work, and
   * a row that leads somewhere a nodal has no business starting from is chrome that charges
   * rent — this file's own argument against a `NavGroup`, applied to the rows themselves.
   *
   * ⚠️ IT SUBTRACTS AND CANNOT ADD. `visibleSections` ANDs it with the capability check, so
   * `superAdminOnly` on a row a role could not see anyway changes nothing, and there is no
   * value of it that reveals a row the table refuses.
   *
   * ⚠️ AND IT HIDES A DOOR, NEVER A ROUTE. The routes keep their existing `RequireCapability`
   * and the API is untouched: a nodal still holds `org.read`, `place.read` and `policy.read`,
   * still needs all three elsewhere in the product, and a bookmarked URL still opens. If that
   * ever needs to stop, it is a capability decision and it does not belong in this file.
   *
   * ⚠️ [amended C5c §3] "A NODAL'S SIDEBAR CARRIES … AND NOTHING ELSE" HAS ONE EXCEPTION NOW: an
   * HQ nodal admin authors policies, so the Policies row moved to `policyAuthorsOnly` below.
   */
  superAdminOnly?: boolean;
  /**
   * ⚠️ [C5c §3] SHOW THIS ROW ONLY TO A POLICY AUTHOR — `mayAuthorPolicy`, THE CAPABILITY AND THE SEAT.
   *
   * The grant needs a door: an HQ nodal admin may create, update, publish and discard a policy
   * draft, and `superAdminOnly` on this row would have left them a capability with no way to it
   * but a typed URL. That narrows [C1g.1]'s client decision for this one row, and says so.
   *
   * ⚠️ A BOOLEAN NAMING ONE IMPORTED PREDICATE, NEVER A ROLE LIST — the header's rule. It SUBTRACTS:
   * `visibleSections` ANDs it with the capability check, so no value of it reveals a row the table
   * refuses. And the predicate reads the officer's org unit type, so a caller that passes none — the
   * closed direction — hides the row from every nodal.
   */
  policyAuthorsOnly?: boolean;
  /**
   * ⚠️ [P7B.1 §4] SHOW THIS ROW ONLY TO A TEMPLATE AUTHOR — `mayUseTemplatesAt`, `arrangement.write` AND
   * a commissionerate seat, the route's own `RequireTemplateAuthor` and the server's
   * `assertMayRunTemplates`. A fourth conjunct, ANDed like the other two: it can only subtract.
   */
  templateAuthorsOnly?: boolean;
  /**
   * ⚠️ [P7C §2] SHOW THIS ROW ONLY TO A DUTY ANALYTICS READER — `mayReadDutyAnalyticsAt`: a dashboard
   * role acting at the commissionerate or a division, or a super admin at any seat. The route's own
   * `RequireDutyAnalyticsReader` and the server's level gate read the same rule. A FIFTH conjunct,
   * ANDed like the other three, so it can only subtract; a caller passing no seat hides the row from
   * every nodal, the closed direction.
   */
  dutyAnalyticsReadersOnly?: boolean;
  /**
   * Match this row's path exactly, for the index route — `NavLink`'s `end`. [amended TYPED-BACK] The sidebar
   * renders every row as a `Link` and copies `NavLink`'s own rule (`AppSidebar.tsx`), so this means the same.
   */
  end?: boolean;
};

export type NavGroup = {
  kind: 'group';
  id: string;
  label: string;
  icon: LucideIcon;
  capability?: Capability;
  children: NavLeaf[];
};

export type NavEntry = NavLeaf | NavGroup;

export type NavSection = {
  id: string;
  /** The small muted uppercase label. Omitted on the first section. */
  label?: string;
  /** The optional trailing `+`. */
  action?: { label: string; to: string; capability: Capability };
  entries: NavEntry[];
};

/**
 * ⚠️ `Organisation` AND `Officers` MUST STAY TOP-LEVEL LEAVES.
 *
 * A collapsed group removes its children from the DOM, and `AppShell.test.tsx` asserts
 * `getByRole('link', { name })` for both on every role that holds the capability. Whether
 * a row is grouped is a rendering decision; that assertion is a contract.
 * `navigation.test.ts` pins it so the two cannot be confused.
 */
/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1f §9] THE ARRANGEMENTS LIST'S OWN WORDS, EXPORTED SO A BREADCRUMB CANNOT DRIFT.
 *
 *  ⚠️ THE DEFAULT IS THE MAJORITY WORD AND THE OVERRIDE IS THE EXCEPTION. Three of the four
 *  dashboard roles read "View All PA", so it is one override rather than three — and a role
 *  added to the table later inherits the narrower-sounding word, which is the fail-safe
 *  direction for a label.
 *
 *  ⚠️ AND THE PREMISE THE BRIEF GAVE FOR THIS IS FALSE, WHICH IS REPORTED RATHER THAN WORKED
 *  AROUND. §9 said a nodal admin "does not need Manage Arrangements, because they cannot
 *  raise one". They can: `mayAuthorArrangement` refuses only a UNIT, and `arrangement.write`
 *  is `['SUPER_ADMIN','NODAL_ADMIN']` — whose own header reads "A DIVISIONAL nodal admin
 *  holds this too." So this is a RENAME and nothing more; the capability table is untouched,
 *  the `+ New arrangement` action is untouched, and a divisional nodal still raises orders
 *  from a row that now calls itself a view. That tension is the client's to resolve, and
 *  removing the row would have been a capability change made in a nav file.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
/**
 * ⚠️ [P7B.1 §4] ONE ICON PER TYPED ARRANGEMENT, AND THE RECORD IS EXHAUSTIVE — so a type registered
 * later must choose its own glyph, which `navigation.test.ts`'s distinct-icon claim then holds apart
 * from every other row. A shared fallback would put two identical icons in one sidebar.
 */
export const TYPED_ARRANGEMENT_ICON: Record<ArrangementType, LucideIcon> = {
  'durga-puja-2026': CalendarRange,
};

export const ARRANGEMENTS_NAV = {
  to: '/arrangements',
  label: 'View All PA',
  labelByRole: { SUPER_ADMIN: 'Manage Arrangements' },
} as const satisfies { to: string; label: string; labelByRole: Partial<Record<AccessRole, string>> };

/**
 * ⚠️ ONE RESOLVER, THREE READERS — the sidebar, the ⌘K palette and both breadcrumbs.
 *
 * `labels.ts` states the rule this protects: a shortcut to the inbox reading anything other
 * than "Assign PA" would be a second vocabulary for one screen. A breadcrumb saying "Manage
 * Arrangements" above a sidebar row saying "View All PA" is that defect exactly, and the only
 * way to prevent it is for both to ask the same function.
 */
export const labelOf = (
  entry: { label: string; labelByRole?: Partial<Record<AccessRole, string>> },
  role: AccessRole | undefined,
): string => (role ? (entry.labelByRole?.[role] ?? entry.label) : entry.label);

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    id: 'overview',
    entries: [
      // `dashboard.access` is the capability that means "may use this product at all",
      // so it is the honest gate for the landing page. Without it an officer whose role
      // is NONE would see one nav row — fail-open, in the one table that must not.
      /*
       * ⚠️ [P6a] IT POINTS AT `/dashboard`, NOT `/`. `/` redirects there, so both work — but a
       * nav row whose `to` is a redirect never matches its own destination and would never
       * show as active. `end` goes with it: `/dashboard` must stay active while the drill page
       * at `/dashboard/tiles/:tileId` is open, which is the child of it.
       */
      {
        kind: 'leaf',
        to: '/dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        capability: 'dashboard.access',
      },
      /*
       * ─────────────────────────────────────────────────────────────────────────────────
       *  ⚠️ [P7C §2] DUTY ANALYTICS — OVERVIEW'S SECOND ROW, BESIDE THE DASHBOARD, NOT IN SYSTEM.
       *
       *  System is "about the deployment rather than the work" and a nodal holds none of it; this page
       *  reads the WORK — who was asked, who named, who reached — beside the Dashboard it drills from.
       *
       *  ⚠️ [amended C1g.1] A NODAL'S SIDEBAR GAINS THIS ROW AT A DIVISION OR THE COMMISSIONERATE. It is
       *  `dutyAnalyticsReadersOnly`, so a unit-level nodal is not offered a page whose request 403s.
       *
       *  ⚠️ NO `end`, so the row stays active on `/duty-analytics/rows`, its drill — the Dashboard row's
       *  own reason. `ChartColumn`, a glyph no other row uses (`navigation.test.ts` pins that).
       * ─────────────────────────────────────────────────────────────────────────────────
       */
      {
        kind: 'leaf',
        to: '/duty-analytics',
        label: 'Duty analytics',
        icon: ChartColumn,
        capability: 'dashboard.access',
        dutyAnalyticsReadersOnly: true,
      },
      /*
       * ─────────────────────────────────────────────────────────────────────────────────
       *  ⚠️ [LOCATION-INTEL] LOCATIONAL INTEL — OVERVIEW'S THIRD ROW, BESIDE DUTY ANALYTICS, BY THE BRIEF.
       *
       *  `LOCATION_INTEL_ACCESS` is `officer.read` AND the officer's own flag, so the row is a holder's alone and
       *  every dashboard role can hold it — the FIRST flag-gated row OUTSIDE System ([LOCATION-INTEL.2]: it said
       *  "second"; Usage analytics and Audit log are both in System). NO new narrowing boolean: a
       *  flag requirement already only subtracts, and the routes refuse a non-holder whatever the sidebar shows.
       *  The label is the brief's own name for the page. `MapPinned`, a glyph no other row uses.
       * ─────────────────────────────────────────────────────────────────────────────────
       */
      {
        kind: 'leaf',
        to: '/location-intel',
        label: 'Locational Intel',
        icon: MapPinned,
        capability: LOCATION_INTEL_ACCESS,
      },
    ],
  },
  /**
   * [P3a] Operations — the product's main work, above the masters it is written in.
   *
   * A section rather than a leaf in Overview: P3b adds round-the-clock arrangements
   * beside this one, and a section that already exists takes a second row for free.
   */
  {
    id: 'operations',
    label: 'Operations',
    action: { label: 'New arrangement', to: '/arrangements/new', capability: 'arrangement.write' },
    entries: [
      /*
       * [P4] ⚠️ FIRST IN THE SECTION, ABOVE THE ORDERS THEMSELVES.
       *
       * FR-5.3.1 calls this the nodal's LANDING screen. For a divisional nodal it is the
       * only screen they open most days — the arrangement list is where orders are RAISED,
       * which is the CP office's work. Ordering the section by who uses what, rather than by
       * which entity is bigger.
       */
      /*
       * ⚠️ [D4.5 §4] "Upcoming PA" → "Assign PA". USER-VISIBLE STRINGS ONLY: the route stays
       * `/inbox`, the page is still `pages/Inbox`, and `queryKeys.inbox` is untouched. The
       * screen is where a nodal DOES the naming rather than merely where they see what is
       * coming, and the same words now appear on the page title, the dashboard tile and the
       * fill screen's breadcrumb — four sites, because neither string has an owning constant.
       */
      { kind: 'leaf', to: '/inbox', label: 'Assign PA', icon: ListTodo, capability: 'arrangement.read' },
      /*
       * ⚠️ [D4.5 §4] "Arrangements" → "Manage Arrangements", so the pair reads as a division
       * of labour: ASSIGN the ones addressed to you, MANAGE the ones this office raises. The
       * page's own title stays "Police arrangements" — §4 renames the sidebar, not the page.
       */
      {
        ...ARRANGEMENTS_NAV,
        kind: 'leaf',
        icon: CalendarClock,
        capability: 'arrangement.read',
      },
      /*
       * [P3b.1] The second row this section was created for. `Repeat`, not another clock:
       * `CalendarClock` is the row directly above, two clock glyphs in a short section is
       * how a nav stops being scannable, and "it keeps going" is the thing that actually
       * distinguishes a standing order.
       */
      /*
       * ⚠️ [C1g.1] HIDDEN FROM NODAL ROLES, AND THE ROW IS LIVE CODE RATHER THAN A COMMENT.
       *
       * "For now" — a client decision, and the CP office keeps it. `surfaces.ts` is this
       * repository's hide-not-delete record and is deliberately NOT used: its entries are
       * build-time constants that hide a control from EVERYBODY, and this hides one from three
       * roles out of four. A commented-out leaf would have done the same thing to a super
       * admin, who is the one person who raises a standing naka.
       */
      {
        kind: 'leaf',
        to: '/standing-orders',
        label: 'Round the clock',
        icon: Repeat,
        capability: 'standingOrder.read',
        superAdminOnly: true,
      },
      /*
       * ─────────────────────────────────────────────────────────────────────────────────
       *  ⚠️ [P7B.1 §4] A TYPED ARRANGEMENT IS ITS OWN ROW — "Durga Puja PA" — AND THE TEMPLATES ITS OWN
       *  ROW BESIDE IT. Both reverse P7B: the typed list was a fourth option on Manage Arrangements'
       *  `Segmented`, and `/templates` had "no sidebar row, deliberately".
       *
       *  ⚠️ THE TYPED ROW IS FOR EVERY ROLE THAT HELD THE TAB — `arrangement.read`, no narrowing — and
       *  that AMENDS [C1g.1]'s client decision that a nodal's sidebar is "the dashboard, the two
       *  arrangement lists and Officers": it is three lists now. Hiding the row from a nodal would have
       *  removed the only door a nodal had to the typed orders, which is a capability change made in a
       *  nav file — the thing [C1f §9] refused.
       *
       *  ⚠️ TEMPLATES IS FOR A TEMPLATE AUTHOR ONLY (`templateAuthorsOnly`). Nav visibility is not
       *  authorisation: the route keeps `RequireTemplateAuthor`, and every `/templates` request keeps
       *  `assertMayRunTemplates`, which reads the seat by POSITION.
       * ─────────────────────────────────────────────────────────────────────────────────
       */
      ...ARRANGEMENT_TYPE_KEYS.map((key): NavLeaf => ({
        kind: 'leaf',
        to: typedListHref(key),
        label: ARRANGEMENT_TYPES[key].label,
        icon: TYPED_ARRANGEMENT_ICON[key],
        capability: 'arrangement.read',
      })),
      {
        kind: 'leaf',
        to: '/templates',
        label: 'Templates',
        icon: BookCopy,
        capability: 'arrangement.write',
        templateAuthorsOnly: true,
      },
    ],
  },
  {
    id: 'directory',
    label: 'Directory',
    entries: [
      /*
       * ⚠️ [C1g.1] THE ESTABLISHMENT IS THE CP OFFICE'S OWN WORK, so a nodal's Directory is
       * Officers and nothing else. Both rows keep `org.read` / `place.read` — the capabilities
       * are untouched, and a nodal needs them for the arrangement builder's unit picker and its
       * place autocomplete, which is exactly why this could not be done by narrowing the table.
       */
      {
        kind: 'leaf',
        to: '/org-tree',
        label: 'Organisation',
        icon: Building2,
        capability: 'org.read',
        superAdminOnly: true,
      },
      { kind: 'leaf', to: '/officers', label: 'Officers', icon: Users, capability: 'officer.read' },
      /**
       * [amended P1b] The place master joins Directory rather than getting a section of
       * its own. A section is a GROUPING — a hairline and a muted uppercase label above
       * one row is chrome for nothing — and a gazetteer is a directory master in exactly
       * the sense the other two are.
       */
      /*
       * ⚠️ [C3c §13b] THE PLACES ROW IS GONE FROM THE NAV, AND NOTHING ELSE ABOUT PLACES IS.
       *
       * `superAdminOnly` is ANDed with the capability filter and can therefore only ever subtract
       * from a ROLE — "hidden from everyone, including the super admin" is not a role narrowing,
       * so the honest edit is the row itself. A `surfaces.ts` entry would be wrong here for once:
       * `visibleSections` is a derivation over a data literal, so removing an entry exercises no
       * new code path and a stubbed pair would have nothing to assert — and that file's own rule
       * is that what keeps a hidden branch alive is a TEST.
       *
       * ⚠️ `/places` STAYS ROUTED, behind `place.read`. A bookmark landing on `/forbidden` would
       * claim a refusal the API does not make — the officer genuinely holds the capability.
       *
       * ⚠️ AND `place.read` ITSELF IS UNTOUCHED, because `PlaceAutocomplete` needs it on every
       * arrangement this product raises. That is the same reason this section's header already
       * gives for `org.read`: removing a door is not removing the room.
       */
    ],
  },
  /**
   * ⚠️ [amended P2] A SECTION, NOT A `NavGroup`, AND THAT WAS A DECISION.
   *
   * A `NavGroup` is a disclosure: it costs a click on every visit and it REMOVES ITS
   * CHILDREN FROM THE DOM while collapsed — the same property this file already warns
   * about for Organisation and Officers. Hiding three of the product's six destinations
   * behind a chevron, in a sidebar with abundant vertical room, is chrome that charges
   * rent.
   *
   * P1b's note above says "a hairline and a muted label above ONE row is chrome for
   * nothing". Three rows is a grouping, which is exactly what a section is for.
   *
   * `NavGroup` is not thereby dead: `AppSidebar` renders it and two tests exercise its
   * pruning. It is waiting for a section that genuinely exceeds ~7 rows, which P3 and P6
   * will produce. Populating it early to justify its existence is the tail wagging the
   * dog.
   *
   * "Configuration", not "Masters": Places IS a master and it lives in Directory, so the
   * word would divide nothing. PA_REQUIREMENTS §3 supplies the right one for free —
   * "everything is configuration, not code."
   *
   * Policies first: it is the destination, and the other two are its vocabulary.
   */
  {
    id: 'configuration',
    label: 'Configuration',
    entries: [
      /*
       * ⚠️ [C5c §3] `policyAuthorsOnly`, NOT `superAdminOnly` — an HQ nodal admin authors policies, so
       * their Configuration section is this one row (System showing `Settings` alone is the
       * precedent for a section of one). `capability` stays `policy.read`, the key the list route
       * takes; the narrowing is who is offered the DOOR, and a divisional nodal still opens the URL.
       */
      {
        kind: 'leaf',
        to: '/policies',
        label: 'Policies',
        icon: ClipboardList,
        capability: 'policy.read',
        policyAuthorsOnly: true,
      },
      {
        kind: 'leaf',
        to: '/post-types',
        label: 'Post types',
        icon: ListChecks,
        capability: 'postType.read',
        superAdminOnly: true,
      },
      {
        kind: 'leaf',
        to: '/vehicle-types',
        label: 'Vehicle types',
        icon: Truck,
        capability: 'vehicleType.read',
        superAdminOnly: true,
      },
      /*
       * ⚠️ [P6b; last again since ANALYTICS §5] `Settings` sat below this row from [C1h] until it
       * moved to System, so the section is four rows once more.
       *
       * ⚠️ [P6b] LAST, AND STILL A SECTION RATHER THAN A `NavGroup`. Four rows does not reach
       * the ~7 the paragraph above reserves a disclosure for, and the argument against one — a
       * click every visit, children removed from the DOM — is unchanged at four.
       *
       * ⚠️ "Dashboard tiles", NOT "Dashboard layouts". The other three rows name the THING
       * configured, and a row reading "Dashboard" in a sidebar that already has one at the top
       * is two links that look like the same destination.
       *
       * ⚠️ AND `LayoutGrid`, NOT `LayoutDashboard` — that glyph belongs to the Overview row.
       * Two identical icons in one sidebar is the objection this file already makes elsewhere.
       *
       * ⚠️ GATED ON THE WRITE CAPABILITY, which has no read half. `/policies/new`'s convention:
       * a screen with no read-only meaning gets the write key, because a `/forbidden` bounce is
       * a better answer than a configurator that cannot be saved.
       */
      {
        kind: 'leaf',
        to: '/dashboard-layouts',
        label: 'Dashboard tiles',
        icon: LayoutGrid,
        capability: 'dashboard.configure',
      },
    ],
  },
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  [ANALYTICS §5] SYSTEM — THE ROWS THAT ARE ABOUT THE DEPLOYMENT RATHER THAN THE WORK.
   *
   *  ⚠️ NOT CONFIGURATION, AND THAT IS WHY SETTINGS MOVED HERE. Configuration's rows are the
   *  VOCABULARY an arrangement is written in — a policy, its post and vehicle types, the tiles a
   *  role opens on — which PA_REQUIREMENTS §3 calls "configuration, not code". Usage analytics
   *  configures nothing: it reads how the system is used, and the system-wide settings store is
   *  a property of the deployment rather than of any order. A usage page under "Configuration"
   *  would read as a thing an operator tunes.
   *
   *  ⚠️ A SECTION RATHER THAN A SIXTH CONFIGURATION ROW. Configuration keeps its four rows and
   *  this one holds three [ANALYTICS §8: the Audit log joined it], so both stay well under the ~7 rows the Configuration header reserves a
   *  `NavGroup` for, and neither pays a disclosure's click on every visit.
   *
   *  ⚠️ LAST IN THE SIDEBAR: a SUPER_ADMIN visits these least, and a nodal holds none of their
   *  capabilities, so for three roles out of four the section is not rendered at all —
   *  `visibleSections` drops a section left with no entries, hairline and label with it.
   *
   *  ⚠️ [amended AUDIT-FLAG] AND FOR A SUPER ADMIN WITHOUT `access_analytics` IT RENDERS WITH
   *  `Settings` ALONE. Two of its three rows name a flag now, and `visibleSections` prunes a section
   *  only at ZERO entries — so the shipped default state, every super admin until an operator grants
   *  the flag, is this header over one row. Stated because it looks like a half-broken section and is
   *  not one: `docs/ops/analytics.md` §5.1 is the grant.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  {
    id: 'system',
    label: 'System',
    entries: [
      /*
       * ⚠️ [ANALYTICS §8] FIRST IN SYSTEM. The audit trail has been recorded since [P1b] and had no
       * reader until this row; it is the record of who changed the establishment, the masters and the
       * orders, so it leads the section rather than trailing the telemetry below it.
       *
       * ⚠️ [amended AUDIT-FLAG] AND THE OFFICER'S OWN FLAG: `AUDIT_ACCESS` is `audit.read` AND
       * `access_analytics` — the same one flag Usage analytics takes, by a client decision that
       * reverses [C5b §5]. So a super admin who has not been granted it no longer sees this row, and
       * `GET /audit` refuses them; nav visibility is not authorisation, and the one value is what
       * makes the door and the room agree.
       *
       * ⚠️ `ScrollText`, a glyph no other row uses — `navigation.test.ts` pins that every destination's
       * icon is distinct.
       */
      {
        kind: 'leaf',
        to: '/audit-log',
        label: 'Audit log',
        icon: ScrollText,
        capability: AUDIT_ACCESS,
      },
      /*
       * ⚠️ `analytics.read`, WHICH IS SUPER_ADMIN. The page shows network addresses, refusal
       * clusters and request volumes across the whole force; nav visibility is not
       * authorisation, and `GET /analytics` and the route check the same capability.
       *
       * ⚠️ [amended C5b §5] AND THE OFFICER'S OWN FLAG: `ANALYTICS_ACCESS` is `analytics.read` AND
       * `access_analytics`, so a super admin who has not been granted it no longer sees the row —
       * and the route and both API routes refuse them, so the door and the room agree.
       *
       * ⚠️ `Activity`, a glyph no other row uses — `LayoutDashboard` is the Overview row's and
       * `LayoutGrid` the tile configurator's, and two identical icons in one sidebar is the
       * objection this file already makes for `Dashboard tiles`.
       */
      {
        kind: 'leaf',
        to: '/usage-analytics',
        label: 'Usage analytics',
        icon: Activity,
        capability: ANALYTICS_ACCESS,
      },
      /*
       * ⚠️ [C1h §1; moved ANALYTICS §5] LAST IN SYSTEM, AND IT WAS LAST IN CONFIGURATION UNTIL
       * THIS SECTION EXISTED. The section header above says why it moved.
       *
       * ⚠️ "Settings", NOT "Appearance". The row names the SCREEN, and the screen is the system
       * settings store — whose whole design premise is that key #2 is a write rather than a
       * migration. A row named after today's only key is a row that has to be renamed the first
       * time that premise is used.
       *
       * ⚠️ AND IT IS GATED ON `settings.write`, WHICH HAS NO READ HALF — `/dashboard-layouts`'
       * convention, for its reason: a screen with no read-only meaning gets the write
       * key. Nav visibility is not authorisation; the route and both endpoints check the same
       * capability.
       */
      {
        kind: 'leaf',
        to: '/settings',
        label: 'Settings',
        icon: SlidersHorizontal,
        capability: 'settings.write',
      },
    ],
  },
];

/**
 * Capability filtering, depth-first. A group whose children all vanish vanishes with
 * them; a section left with no entries is not rendered, so its hairline and its label do
 * not appear over nothing.
 */
export const visibleSections = (
  role: AccessRole | undefined,
  sections = NAV_SECTIONS,
  /**
   * ⚠️ [C1c §7] THE CALLER'S OWN ORG UNIT TYPE, AND IT IS A THIRD PARAMETER RATHER THAN A
   * CAPABILITY BECAUSE IT CANNOT BE ONE.
   *
   * There is no "unit nodal" ROLE — a `NODAL_ADMIN` at a police station and one at a division
   * hold identical capabilities, and only the node they are posted at differs. Raising an
   * arrangement is a division's work (§7, confirmed with the client), so the `+` beside
   * Operations is hidden for a unit rather than leading to a form whose source picker is empty.
   *
   * ⚠️ OPTIONAL, SO EVERY EXISTING CALLER AND TEST IS UNCHANGED. Absent means "not a unit",
   * which is what every caller before C1c meant.
   *
   * ⚠️ [amended C5c §3] AND NOW ALSO "NOT THE COMMISSIONERATE": the Policies row follows
   * `mayAuthorPolicy`, which reads the seat, so absent hides it from every nodal — the closed
   * direction. `AppShell` and the ⌘K palette both pass the officer's own type.
   */
  orgUnitType?: string,
  /**
   * ⚠️ [UX1 §8] THE DEPLOYMENT'S `arrangement.nodalAuthor` SETTING, AND IT DEFAULTS TO `false`
   * — the value `DEFAULT_NODALS_MAY_AUTHOR_ARRANGEMENTS` carries, and the CLOSED direction.
   *
   * A fourth parameter rather than a capability, for `orgUnitType`'s reason one line up: it is
   * not a property of the ROLE. It is optional so the ~40 existing call sites and tests that
   * pass neither it nor `orgUnitType` are unchanged — and the default it takes is the one that
   * hides the action, which is the direction a forgotten argument should fail in.
   */
  nodalsMayAuthor = false,
  /**
   * ⚠️ [C5b §5] THE OFFICER'S OWN FLAGS, FROM `/auth/me`, AND THEY DEFAULT TO NONE — the closed
   * direction, for `nodalsMayAuthor`'s reason one line up: a caller that forgets them hides the
   * one row that needs one rather than revealing it. A fifth parameter rather than part of the
   * role because a flag is a property of the PERSON, and `can()` stays about the role.
   */
  flags: readonly OfficerFlag[] = [],
): NavSection[] => {
  if (!role) return [];
  /* ⚠️ [amended C5b §5] The one resolver — a bare capability answers what `can()` answered. */
  const allowed = (capability?: CapabilityRequirement): boolean =>
    !capability || meetsRequirement({ accessRole: role, flags }, capability);
  /*
   * ⚠️ [C1g.1] A SECOND, PURELY SUBTRACTIVE GATE — AND THE `&&` IS WHY IT IS SAFE.
   *
   * It is ANDed with `allowed` at every site below, so there is no value of `superAdminOnly`
   * that can REVEAL a row the capability table refuses; it can only ever hide one more. The
   * header records why that asymmetry is the whole argument.
   */
  /* ⚠️ [C5c §3] `policyAuthorsOnly` is a third conjunct, ANDed like the second — it cannot grant. */
  /* ⚠️ [P7B.1 §4] `templateAuthorsOnly` is a fourth, ANDed the same way — it cannot grant either. */
  /* ⚠️ [P7C §2] `dutyAnalyticsReadersOnly` is a fifth, ANDed the same way — it cannot grant either. */
  const shown = (entry: {
    capability?: CapabilityRequirement;
    superAdminOnly?: boolean;
    policyAuthorsOnly?: boolean;
    templateAuthorsOnly?: boolean;
    dutyAnalyticsReadersOnly?: boolean;
  }): boolean =>
    allowed(entry.capability) &&
    (entry.superAdminOnly !== true || role === 'SUPER_ADMIN') &&
    (entry.policyAuthorsOnly !== true || mayAuthorPolicy(role, orgUnitType)) &&
    (entry.templateAuthorsOnly !== true || mayUseTemplatesAt(role, orgUnitType)) &&
    (entry.dutyAnalyticsReadersOnly !== true || mayReadDutyAnalyticsAt(role, orgUnitType));

  return sections.flatMap((section) => {
    /* ⚠️ [C1f §9] The word is resolved HERE, so `AppSidebar` and the ⌘K palette both get it. */
    const worded = (leaf: NavLeaf): NavLeaf => ({ ...leaf, label: labelOf(leaf, role) });

    const entries = section.entries.flatMap<NavEntry>((entry) => {
      if (!shown(entry)) return [];
      if (entry.kind === 'leaf') return [worded(entry)];

      const children = entry.children.filter(shown).map(worded);
      return children.length > 0 ? [{ ...entry, children }] : [];
    });

    if (entries.length === 0) return [];

    // `action` is destructured OUT before the spread. Spreading `section` and then
    // conditionally re-adding would leave the original in place whenever the role may not
    // perform it — a fail-open bug that looks like a fail-closed one.
    const { action, ...rest } = section;
    /*
     * ⚠️ [amended UX1 §8] THE IMPORTED PREDICATE, NOT A COPY.
     *
     * This line read `const mayRaise = orgUnitType !== 'UNIT';` under a comment claiming it was
     * "the same predicate `RequireDivision` uses". It was a hand-written DUPLICATE, and it would
     * not have moved when §8 gave that predicate a third input — so the `+` beside Manage
     * Arrangements would have gone on answering the pre-UX1 rule while the route it opens
     * refused. [WINGS.1]'s failure mode exactly: two literals for one fact, and the forgotten
     * half fails silently.
     */
    /*
     * ⚠️ AND IT IS SCOPED TO THE ARRANGEMENT ACTION, WHICH FIXES A LATENT BUG IN [C1c §7].
     *
     * That pass applied its `orgUnitType !== 'UNIT'` test to EVERY section's action, and it was
     * harmless only because `NAV_SECTIONS` has exactly one — so a unit nodal would have lost an
     * "Add unit" action too, for a reason about arrangements. `mayAuthorArrangement` narrows ONE
     * verb; gating an unrelated action on it is the same category error as aliasing two
     * predicates that agree today, and §8 turned the latent version into a live one by giving
     * the predicate a term that is `false` by default.
     */
    const raisesAnArrangement = action?.capability === 'arrangement.write';
    const permitted =
      action &&
      allowed(action.capability) &&
      (!raisesAnArrangement || mayAuthorArrangement(role ?? 'NONE', orgUnitType, nodalsMayAuthor))
        ? action
        : undefined;
    return [{ ...rest, entries, ...(permitted ? { action: permitted } : {}) }];
  });
};

/**
 * Every destination a role can reach, flattened — what the ⌘K palette searches.
 *
 * ⚠️ [amended C5b §5] AND THE OFFICER'S FLAGS, or the palette would disagree with the sidebar about
 * Usage analytics. Defaulted to none, the closed direction, like `visibleSections`' own.
 *
 * ⚠️ [amended C5c §3] AND THE OFFICER'S ORG UNIT TYPE, or the palette would disagree with the sidebar
 * about Policies — it passed none before this pass, which was harmless only while nothing in the nav
 * followed the seat. Absent hides the row, the closed direction again.
 */
export const visibleDestinations = (
  role: AccessRole | undefined,
  flags: readonly OfficerFlag[] = [],
  orgUnitType?: string,
): NavLeaf[] =>
  visibleSections(role, NAV_SECTIONS, orgUnitType, false, flags).flatMap((section) =>
    section.entries.flatMap((entry) => (entry.kind === 'leaf' ? [entry] : entry.children)),
  );
